import moment from "moment-timezone";
import { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ApiResponse } from "../../../../api/apiResponse";
import { changeScheduleRequests } from "../../../../api/endpoints/students/requests";
import { indexStudentFormatSchedules } from "../../../../api/endpoints/students/schedules";
import CalendarScheduleRequest from "../../../../api/models/CalendarScheduleRequest";
import { SlotStates } from "../../../../api/models/ScheduleSlotModel";
import { ToastContext } from "../../../../App";
import AppButton from "../../../../components/buttons/AppButton";
import ScheduleCalendar from "../../../../components/calendar/ScheduleCalendar";
import DropDown from "../../../../components/forms/dropdown/DropDown";
import ConfirmationModal from "../../../../components/modal/ConfirmationModal";
import { useAuth } from "../../../../context/AuthContext";
import ApiDateParser from "../../../../helpers/parsers/apiDateParser";
import { useAvailabilitySchedule } from "../../../../hooks/useAvailabilitySchedule";
import useCalendarViewDate from "../../../../hooks/useCalendarViewDate";
import { useTelawah } from "../../../../hooks/useTelawah";
import CalendarScheduleModel from "../../../../models/CalendarScheduleModel";
import CalendarScheduleModelRequest from "../../../../models/CalendarScheduleModelRequest";
import CalendarScheduleSlotModel from "../../../../models/CalendarScheduleSlotModel";
import SchedulingService from "../../../../services/schedulingService";
import TimeZoneAlert from "../../../../components/alerts/timeZone";

export default function RescheduleAppointments() {
  const LocalBasePath = "page.candidate.schedule";
  const { t, i18n } = useTranslation("", { keyPrefix: LocalBasePath });
  const authContext = useAuth();
  const navigate = useNavigate();
  const [availabilitySchedule, refetchAvailabilitySchedule] =
    useAvailabilitySchedule();
  const { telawahOptions = [] } = useTelawah();
  const [selectedTelawahId, setSelectedTelawah] = useState<
    number | string | null | undefined
  >(authContext?.currentUser?.telawah_id);
  const { viewingDate, onChangeViewingDate } = useCalendarViewDate();
  const [schedule, setSchedule] = useState<object>({});
  const { currentUser }: any = useAuth();
  const [selectedSlots, setSelectedSlots] = useState<
    CalendarScheduleSlotModel[]
  >([]);
  const [, updateState] = useState<Object>();
  const toast = useContext(ToastContext);

  const forceUpdate = useCallback(() => updateState({}), []);
  const [isDone, setIsDone] = useState(false);
  const [selectedSessions, setSelectedSessions] =
    useState<CalendarScheduleModel | null>(null);
  const getUserSchedule: any = async () => {
    const UserScheduleApi = await indexStudentFormatSchedules();
    if (UserScheduleApi.isSuccess) {
      return UserScheduleApi.data;
    }
  };

  useEffect(() => {
    fetchAndSetSchedule();
  }, []);

  const fetchAndSetSchedule = async () => {
    let fetchedSchedule = await getUserSchedule();
    setSchedule(fetchedSchedule);
  };
  const mergeSchedules = () => {
    const calendarAvailabilitySchedule = new CalendarScheduleModelRequest(
      availabilitySchedule,
      SlotStates.available
    );
    let calendarRecitationSchedule = new CalendarScheduleModelRequest(
      schedule,
      SlotStates.available
    );
    calendarRecitationSchedule.slots = calendarRecitationSchedule.slots.filter(
      (slot) => !slot.isCandidateSlot
    );
    // calendarRecitationSchedule.modifySlotsStateByDate(viewingDate);

    /**
     * The order of spreading schedules is important as we may have duplication or different slots with the same start and end time
     * The last added slots will override the first slots in the UI if there is duplication
     * So, we give here the higher priority to the recitation schedule
     */
    let calendarSchedule = new CalendarScheduleModelRequest({});
    calendarSchedule.slots = [
      ...calendarAvailabilitySchedule.slots,
      ...calendarRecitationSchedule.slots,
    ];
    setSelectedSessions(calendarSchedule);
  };

  const onSlotClick = (slot: any) => {
    let finalSelected: any = [];
    let schedulingService = new SchedulingService(
      selectedSessions!,
      currentUser
    );
    schedulingService.toggleSlot(slot);
    setSelectedSessions(schedulingService.schedule);
    finalSelected = schedulingService.schedule.slots;

    let selected: any = finalSelected.map((slot: any) => {
      if (slot?.state === 2) return slot;
    });
    setSelectedSlots(selected);
    forceUpdate();
  };

  const handleSubmit = async () => {
    const newSlots = selectedSlots
      .filter((o) => !!o)
      .map((slot) => slot.toApiPayload());
    const response: ApiResponse = await changeScheduleRequests({
      schedule_slots_attributes: newSlots,
    });
    if (response.isSuccess) {
      setIsDone(true);
    } else {
      response.errorMessages.forEach((errorMessage) => {
        toast?.error(errorMessage);
      });
      setIsDone(false);
    }
  };

  const handleCloseModal = () => {
    setIsDone(false);
    navigate("/student/dashboard/appointments-requests");
  };
  useEffect(() => {
    if (!!schedule) mergeSchedules();
  }, [schedule, availabilitySchedule]);

  return (
    <div className="relative flex flex-col w-screen h-screen divide-y divide-gray-200">
      <div className="flex flex-col justify-between gap-5 p-6 pb-4">
        <h2 className="text-lg font-semibold leading-8 text-gray-900">
          {t("changeSchedule")}
        </h2>
        <div className="my-0.5">
          <TimeZoneAlert />
        </div>
        <DropDown
          name={t("TelawahFilter")}
          setValue={(val: any) => setSelectedTelawah(val)}
          defaultValue={`${selectedTelawahId}`}
          label={t("telawah")}
          placeholder={t("chooseTelawah")}
          error={false}
          options={telawahOptions}
          className="w-full"
          disabled
        />
        <p className="text-xs font-medium text-gray-400">{t("disclaimer")}</p>
      </div>
      <div className="w-full overflow-y-auto">
        <ScheduleCalendar
          selectedDate={viewingDate}
          viewingDate={viewingDate}
          schedule={selectedSessions}
          onChangeViewingDate={onChangeViewingDate}
          onSlotClick={onSlotClick}
          compact={true}
          showDates={false}
          highlightSelectedDate={false}
          cancel={false}
        />
      </div>
      {!!selectedSlots.length && (
        <div className="sticky bottom-0 z-20 flex items-center justify-end w-full px-6 py-4 bg-white border-t border-gray-200">
          {/* <div className="sticky flex justify-end flex-shrink-0 gap-4 px-6 py-4"> */}
          <AppButton buttonShape="primary" onClick={handleSubmit}>
            {t("send")}
          </AppButton>
        </div>
      )}
      <ConfirmationModal
        btnText="requestPreview"
        isOpen={isDone}
        onClose={handleCloseModal}
        email={authContext?.currentUser?.email || ""}
      />
    </div>
  );
}
